import type { Dayjs } from 'dayjs'
import { useQuery } from '@tanstack/react-query'

import type { FieldMask } from '@jotta/grpc-connect-openapi/mask'
import type { PartialMessage } from '@jotta/types/compat'

import type { CollectionRef } from '../../../api/useCollection'
import type { SelectablePhoto } from '../../../store/SelectionStore'

import { usePhotoCollectionService } from './useTimelineStream'
import { toNanos } from './util'

const mask: PartialMessage<FieldMask> = {
  includes: [
    {
      fieldNumber: 1, // photo
      childMask: {
        includes: [
          { fieldNumber: 1 }, // idDeprecated
          { fieldNumber: 4 }, // ref
          { fieldNumber: 6 }, // timestampNanos
          { fieldNumber: 19 }, // encodedContentRef
        ],
      },
    },
  ],
}

export function usePhotoIdsForDay(
  collectionRef: CollectionRef,
  day: Dayjs,
  {
    enabled,
  }: {
    enabled: boolean
  },
) {
  const service = usePhotoCollectionService()
  return useQuery({
    refetchOnMount: false,
    enabled,
    queryKey: ['photo_ids_for_day', collectionRef, day.toString()],
    queryFn: async () => {
      const startExclusiveTimestampNanos = toNanos(day.startOf('day'))
      const stopExclusiveTimestampNanos = toNanos(day.endOf('day'))

      const restOfDayResponse = service.getCollectionPhotosMasked(
        {
          collectionRef,
          mask,
          selection: {
            case: 'range',
            value: {
              startExclusiveTimestampNanos,
              stopExclusiveTimestampNanos,
            },
          },
        },
        {
          timeoutMs: 60_000,
        },
      )

      const photos: SelectablePhoto[] = []
      for await (const { photo } of restOfDayResponse) {
        if (!photo) {
          continue
        }
        const { idDeprecated, ref, timestampNanos, encodedContentRef } = photo
        photos.push({
          id: idDeprecated,
          ref,
          timestampNanos,
          encodedContentRef,
        })
      }

      return photos
    },
  })
}
